/**
 * deliveryLedger — what this browser KNOWS about each outbound message
 * (2026-09-08, chat-thread experience plan PR-2).
 *
 * Owner report: "difficult to tell if a message was sent or read". Nothing
 * recorded the facts: the own-tree write was fire-and-forget, the group
 * store's echo was merged and thrown away, a refused dispatch left the bubble
 * looking sent. This ledger is the ONE place those facts land, keyed by
 * `${threadId}|${sharedMessageId}` — the id the server writes the human turn
 * under, minted BEFORE the bubble exists — so every signal from every lane
 * joins the same row:
 *
 *   ownAckedAt    the sender's own per-message doc was acknowledged by
 *                 Firestore's server (the SDK resolves that promise only on
 *                 server ack, offline persistence included)        → "sent"
 *   acceptedAt    prest0nRunTurn answered 2xx (the turn exists server-side;
 *                 in a solo thread that IS delivery)                → "delivered" (solo)
 *   deliveredAt   the shared store echoed the sender's own turn, or the
 *                 summon-suppressed post was accepted               → "delivered" (group)
 *   readAt        a participant's receipt passed the message (PR-3) → "read"
 *   attachmentsMissingAt  the message reached the thread but one or more of
 *                 its files did NOT — the upload produced no durable locator,
 *                 so the server sanitizer dropped the record and every other
 *                 participant sees text only    → "sent · photo didn't · retry"
 *   failedAt      the dispatch never started / the post was refused → "not sent · retry"
 *   queuedOfflineAt  the browser was offline at send; the turn waits in the
 *                 per-thread queue for `online`                     → "waiting for connection"
 *
 * Pure, in-memory, never persisted: a reload renders NO glyph for messages
 * the ledger never saw (a historical message is not "unsent" — it is simply
 * unknown here). Monotonic per fact; `clearFailed` is the one reversal, for a
 * retry that succeeds. Subscribers get one synchronous notification per
 * change; the version counter feeds React's useSyncExternalStore.
 */
export interface DeliveryFacts {
  ownAckedAt?: number;
  acceptedAt?: number;
  deliveredAt?: number;
  readAt?: number;
  failedAt?: number;
  failureReason?: string;
  /**
   * The message landed but its files did not (2026-09-11, Malaika's report).
   *
   * This is NOT a variant of failedAt: the text genuinely reached everyone,
   * and calling that "not sent" would be its own lie. It is the fact the ✓✓
   * was missing — `deriveDeliveryStatus` had no attachment input at all, so a
   * message whose photo the server dropped painted a confident "delivered".
   */
  attachmentsMissingAt?: number;
  /** How many of the message's files did not make it, for the copy. */
  attachmentsMissingCount?: number;
  queuedOfflineAt?: number;
  dispatchedAt?: number;
  /**
   * The message was sent while Prest0n was mid-task and is now ATTACHED to
   * that running task — the server has it, and it folds into the work in
   * progress at the next step (2026-09-15). The receipt for a mid-turn note:
   * state on the sender's own bubble, never a sentence under Prest0n's name.
   */
  notedAt?: number;
  /**
   * Held behind an in-flight turn that could not take a note (no task to
   * attach to, or the attach was refused) — it sends the moment that turn
   * finishes. The one queued send that still exists, and it is never silent.
   */
  queuedBehindTurnAt?: number;
}

export function deliveryKey(threadId: string | null | undefined, sharedMessageId: string | null | undefined): string | null {
  const t = (threadId ?? '').trim();
  const m = (sharedMessageId ?? '').trim();
  return t && m ? `${t}|${m}` : null;
}

type Listener = () => void;

export class DeliveryLedger {
  private facts = new Map<string, DeliveryFacts>();
  private listeners = new Set<Listener>();
  private version = 0;

  get(key: string | null | undefined): DeliveryFacts | undefined {
    return key ? this.facts.get(key) : undefined;
  }

  getVersion(): number {
    return this.version;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  markDispatched(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'dispatchedAt', at);
  }
  markOwnAcked(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'ownAckedAt', at);
  }
  markAccepted(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'acceptedAt', at);
  }
  markDelivered(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'deliveredAt', at);
  }
  markRead(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'readAt', at);
  }
  /**
   * The message reached the thread; `count` of its files did not.
   *
   * Deliberately NOT monotonic-by-`set`, because it carries a count that a
   * partial retry can lower. Cleared by `clearAttachmentsMissing` when a
   * retry gets every file through.
   */
  markAttachmentsMissing(key: string | null | undefined, count: number, at = Date.now()): void {
    if (!key || !(count > 0)) return;
    const current = this.facts.get(key) ?? {};
    this.facts.set(key, { ...current, attachmentsMissingAt: at, attachmentsMissingCount: count });
    this.bump();
  }
  clearAttachmentsMissing(key: string | null | undefined): void {
    if (!key) return;
    const current = this.facts.get(key);
    if (!current || current.attachmentsMissingAt === undefined) return;
    const { attachmentsMissingAt: _a, attachmentsMissingCount: _c, ...rest } = current;
    void _a; void _c;
    this.facts.set(key, rest);
    this.bump();
  }
  markQueuedOffline(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'queuedOfflineAt', at);
  }
  /** Attached to the running task (a mid-turn note the server accepted). */
  markNoted(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'notedAt', at);
  }
  /** Held behind the in-flight turn; sends when it finishes. */
  markQueuedBehindTurn(key: string | null | undefined, at = Date.now()): void {
    this.set(key, 'queuedBehindTurnAt', at);
  }
  /** A refused / never-started send. Cleared by `clearFailed` when a retry gets through. */
  markFailed(key: string | null | undefined, reason: string, at = Date.now()): void {
    if (!key) return;
    const current = this.facts.get(key) ?? {};
    // Delivery already proven ⇒ a late failure signal is about the REPLY, not
    // the message; never downgrade a delivered message to "not sent".
    if (current.deliveredAt !== undefined || current.readAt !== undefined) return;
    this.facts.set(key, { ...current, failedAt: at, failureReason: reason });
    this.bump();
  }
  clearFailed(key: string | null | undefined): void {
    if (!key) return;
    const current = this.facts.get(key);
    if (!current || current.failedAt === undefined) return;
    const { failedAt: _f, failureReason: _r, ...rest } = current;
    void _f; void _r;
    this.facts.set(key, rest);
    this.bump();
  }

  /** Tests only. */
  reset(): void {
    this.facts.clear();
    this.bump();
  }

  private set(key: string | null | undefined, field: keyof DeliveryFacts, at: number): void {
    if (!key) return;
    const current = this.facts.get(key) ?? {};
    // Monotonic: the first stamp of a fact wins; a re-observed echo is not news.
    if (current[field] !== undefined) return;
    this.facts.set(key, { ...current, [field]: at });
    this.bump();
  }

  private bump(): void {
    this.version += 1;
    for (const l of [...this.listeners]) {
      try { l(); } catch { /* a listener's throw must not stop the others */ }
    }
  }
}

/** The one ledger every send path and every renderer shares. */
export const deliveryLedger = new DeliveryLedger();
